import React, { useEffect, useMemo, useState, useRef, useCallback, memo, useContext, useLayoutEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'store';
import { AutoSizer } from 'react-virtualized';
import { VariableSizeList } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import { useLocation } from 'react-router-dom';

import api from 'services/api';
import { fetchVideoTimeline } from 'api/videosAPI';
import { addData, resetTimelineUpdateStatus } from 'store/secondaryVideoTabsDataSlice';
import TimelineItem from './_components/TimelineItem';

import { MODAL_ANIMATIONS, MODAL_TYPES } from 'components/Modal/constants';
import { ModalContext } from 'context/ModalContext';
import { addSpeakers, getSpeaker } from 'store/spearkeSlice';
import { removeTimeline, addTimelineSummary } from 'api/timelineAPI';
import AddForm from './_components/AddForm';

import { seekPlayerTime, getScrollPosition, updateTabContent, getItemSizes } from 'store/secondaryChannelVideoSlice';

interface ITimeline {
  currentVideoShot: number;
  channelId: string;
  videoHashId: string;
  selected: number;
  videoShots: any;
  navigateVideoPage: any;
  aesKeyId: string;
  decryptionKeys: any;
  secondaryViewSlice: any;
  isResizing: boolean;
}

type ItemSizeMap = Map<number, number>;

function TimelineView({
  currentVideoShot,
  channelId,
  videoHashId,
  selected,
  videoShots,
  navigateVideoPage,
  aesKeyId,
  decryptionKeys,
  secondaryViewSlice,
  isResizing
}: ITimeline) {
  const keyId = channelId + videoHashId + currentVideoShot;
  const userData = useSelector((state: RootState) => state.user.user);
  const [editingId, setEditingId] = useState<null | string>(null);
  const [people, setPeople] = useState<any[]>([]);
  const speakerSlice = useSelector((state: RootState) => state.speakers);
  const dispatch = useDispatch();
  const location = useLocation();
  const videoMatch = location.pathname.match(/\/video\/([^/]+)/);
  const activeVideoPageId = videoMatch ? videoMatch[1] : undefined;
  const [hoveredId, setHoveredId] = useState(null as any);

  const scrollPosition = useRef<number>(0);
  const videoShotId = useMemo(() => {
    if (!videoShots?.length) return;
    const idx = videoShots[currentVideoShot]?.shot_hash_id;
    return idx;
  }, [keyId, videoHashId, channelId, videoShots, currentVideoShot]);

  // useEffect(() => {
  //   videoShotIdRef.current = videoShotId;
  // }, [videoShotId]);

  useEffect(() => {
    const speakers = getSpeaker(speakerSlice, videoShotId);
    const fetchPeople = async () => {
      const response = await api.get(`/api/videos/${videoHashId}/shots/${videoShotId}/speakers/`);
      setPeople(response.data);
      dispatch(addSpeakers({ shotId: videoShotId, speakerData: response.data }));
    };

    if (videoShotId && !speakers) {
      fetchPeople();
    } else {
      setPeople(speakers);
    }
  }, [videoShotId, videoHashId, channelId]);

  const { showModal } = useContext(ModalContext);

  const handleEdit = (id: string) => {
    if (editingId !== null && editingId !== id) {
      showModal(MODAL_TYPES.edit_summary, {
        animation: MODAL_ANIMATIONS.fade,
        channelId,
        setEditingId,
        editingId: id,
        itemType: 'timeline'
      });
    } else {
      setEditingId(id);
    }
  };

  const handleSpeaker = (hashId: string) => {
    const timeline = timelines.find((item) => item.hash_id == hashId);
    showModal(MODAL_TYPES.add_speaker, {
      people: people,
      sentence: timeline,
      speaker: timeline.user_info.hash_id,
      video_info_hash_id: videoHashId,
      videoshot_hash_id: videoShotId,
      sentence_hash_id: timeline.hash_id,
      inTimeline: true
    });
  };

  const handleDelete = (timelineHashId: string) => {
    const confirmDelete = async () => {
      await removeTimeline(videoHashId, videoShotId, timelineHashId);
    };

    showModal(MODAL_TYPES.remove_summary, {
      animation: MODAL_ANIMATIONS.fade,
      modalMessage: {
        title: 'Deleting Timeline',
        text: 'You are about to to delete a timeline.  Click "Confirm to delete" button.'
      },
      onConfirm: confirmDelete
    });
  };

  const handleAddSummary = async (content: string, timestamp: number) => {
    try {
      await addTimelineSummary(videoHashId, videoShotId, content, timestamp);
    } catch (error) {
      //pass
    }
  };

  const itemSizeMap = useRef<ItemSizeMap>(new Map());
  const listRef = useRef<VariableSizeList | null>(null);
  const secondaryViewSize = useRef();
  const timelineState = useSelector(
    (state: RootState) => state.secondaryViewTab.videos.find((video) => video.videoShotId === videoShotId)?.timelines
  );

  const [timelines, setTimelines] = useState<any[]>([]);
  const [nextUrl, setNextUrl] = useState<string>('');

  const navigateToVideo = (time: any) => {
    const isActivePlayer = sessionStorage.getItem('playerActive');
    if (isActivePlayer === 'true') {
      dispatch(seekPlayerTime({ channelHashId: channelId, time: time }));
    } else {
      navigateVideoPage(time);
    }
  };

  const initTimeline = (results: any, nextUrl: string) => {
    setTimelines((prevData: any[]) => {
      const updatedData = [...prevData, ...results];
      dispatch(
        addData({
          videoHashId: keyId,
          videoShotId: videoShotId,
          key: 'timelines',
          data: {
            results: updatedData,
            nextUrl: nextUrl,
            hasUpdated: false
          }
        })
      );
      return updatedData;
    });

    setNextUrl(nextUrl);
  };

  const fetchNextUrl = (url: string) => {
    setLoading(true);
    api.get(url).then((res) => {
      const { results } = res.data;
      const next = res.data?.links?.next;
      initTimeline(results, next);
      setLoading(false);
    });
  };

  useEffect(() => {
    if (timelineState && timelineState.hasUpdated) {
      setTimelines(timelineState?.results);
    }
  }, [timelineState]);

  const isProgramScroll = useRef<boolean>(false);

  useEffect(() => {
    let mounted = true;
    const defaultScrollTop = getScrollPosition(secondaryViewSlice, channelId, videoHashId, 'timelines');
    const defaultItemSizes = getItemSizes(secondaryViewSlice, channelId, videoHashId, 'timelines');
    const fetchData = async () => {
      if (videoShotId === undefined) return;
      setLoading(true);
      if (mounted) {
        const response = await fetchVideoTimeline(channelId, videoHashId, videoShotId);
        const { results } = response.data;
        const next = response?.data?.links?.next;
        initTimeline(results, next);
        setLoading(false);
      }
    };

    if (timelineState && timelineState?.results) {
      setTimelines(timelineState?.results);
      setNextUrl(timelineState.nextUrl);
      dispatch(resetTimelineUpdateStatus({ videoShotId: videoShotId, status: false }));
      if (defaultItemSizes) {
        itemSizeMap.current = new Map(Object.entries(defaultItemSizes).map(([key, value]) => [Number(key), value]));
      }
      if (defaultScrollTop !== undefined && listRef.current) {
        isProgramScroll.current = true;
        scrollPosition.current = defaultScrollTop;
        listRef.current.scrollTo(defaultScrollTop);
      }
    } else {
      fetchData();
    }

    return () => {
      mounted = false;
    };
  }, [channelId, videoShotId]);

  useLayoutEffect(() => {
    return () => {
      if (scrollPosition.current !== undefined && itemSizeMap.current) {
        const itemSizeObj = Object.fromEntries(itemSizeMap.current);
        dispatchUpdatedContent(scrollPosition.current, itemSizeObj);
        scrollPosition.current = 0;
        listRef.current?.resetAfterIndex(0, true);
        listRef.current = null;
        setTimelines([]);
        setNextUrl('');
      }
    };
  }, [channelId, videoShotId, videoHashId, selected]);

  const [loading, setLoading] = useState<boolean>(false);
  const isItemLoaded = (index: number) => index < timelines.length;
  const loadMoreItems = (startIndex: number, stopIndex: number) => {
    if (!loading && nextUrl) {
      fetchNextUrl(nextUrl);
    }
  };

  const isEditing = useCallback(
    (hashId) => {
      return editingId === hashId;
    },
    [editingId]
  );

  const ItemRenderer = ({ index, style, timeline }: { index: number; style: React.CSSProperties; timeline: any }) => {
    // const timeline = timelines[index];
    let displayAvatar = true;
    const isLastItem = index === timelines?.length - 1;

    const isEdited = isEditing(timeline?.hash_id);
    let itemStyle = {
      ...style
    };

    if (isEdited) {
      itemStyle = {
        ...itemStyle,
        zIndex: 999,
        height: 'auto',
        marginBottom: '10px',
        backgroundColor: '#fff'
      };
    } else {
      itemStyle = {
        ...itemStyle
      };
    }

    if (index > 0 && timeline?.user_info) {
      const prevTimeline = timelines[index - 1];
      if (prevTimeline && prevTimeline.user_info) {
        displayAvatar = prevTimeline.user_info.hash_id !== timeline.user_info.hash_id;
      }
    }

    const itemRef = useCallback(
      (node) => {
        if (node !== null) {
          const height = node.getBoundingClientRect().height;
          const currentSize = itemSizeMap.current.get(index);

          // Only update if the size has changed to prevent unnecessary renders
          if (!currentSize || currentSize !== height) {
            setItemSize(index, height);
          }
        }
      },
      [index]
    );

    if (!timeline) {
      return (
        <div style={{ ...style, height: '40px' }} className="w-full bg-white flex justify-center">
          Loading...
        </div>
      );
    }

    return (
      <div
        style={itemStyle}
        className={`relative w-full ${!isResizing ? 'hover:bg-[#f8f8f8]' : ''}`}
        id={timeline.hash_id}
        key={timeline.hash_id}
      >
        <div ref={itemRef} className="py-[4px]">
          <TimelineItem
            key={timeline.hash_id}
            timeline={timeline}
            displayAvatar={displayAvatar}
            userData={userData}
            navigateToVideo={navigateToVideo}
            user={{
              avatar: timeline.user_info?.avatar ? timeline.user_info.avatar : timeline.active_speaker_thumbnail_url,
              display_name: timeline.user_info?.display_name
                ? timeline.user_info?.display_name
                : timeline.user_info?.name || 'Unknown',
              email: timeline.user_info?.email,
              name: timeline.user_info?.name,
              id: timeline.user_info?.user_id
            }}
            aesKeyId={aesKeyId}
            decryptionKeys={decryptionKeys}
            videoHashId={videoHashId}
            channelId={channelId}
            isEditing={editingId === timeline.hash_id}
            videoShotId={videoShotId}
            handleEdit={() => {
              handleEdit(timeline.hash_id);
            }}
            handleCancelEdit={() => {
              setEditingId(null);
            }}
            handleSpeaker={() => handleSpeaker(timeline.hash_id)}
            onDelete={() => handleDelete(timeline.hash_id)}
            isExternal={false}
            secondaryViewSize={secondaryViewSize.current}
            isResizing={isResizing}
            index={index}
            hoveredId={hoveredId}
            handleHoveredId={() => {
              setHoveredId(timeline.hash_id);
            }}
          ></TimelineItem>
        </div>
        {isLastItem && <div className="h-5 w-full"></div>}
      </div>
    );
  };

  const getItemSize = (index: number) => {
    return itemSizeMap.current.get(index) || 50; // default size of 50, item will be resized dynamically
  };
  const setItemSize = (index: number, size: number) => {
    const currentSize = itemSizeMap.current.get(index);
    if (currentSize !== size && listRef.current) {
      // Ensure listRef.current is not null
      itemSizeMap.current.set(index, size + 4); // add 4px for height to make room
      // Optionally, you can force the list to recompute its layout without triggering a re-render
      // This prevents the scrolling behavior from resetting to the top
      listRef.current.resetAfterIndex(index, true);
    }
  };

  const dispatchUpdatedContent = (currentPosition: number, itemSizes: any) => {
    if (currentPosition !== undefined) {
      dispatch(
        updateTabContent({
          channelHashId: channelId,
          videoHashId: videoHashId,
          data: {
            timelines: {
              scrollPosition: currentPosition,
              itemSizes: itemSizes
            }
          }
        })
      );
      scrollPosition.current = 0;
    }
  };

  const handleScrollY = ({ scrollOffset, scrollUpdateWasRequested }: any) => {
    if (scrollOffset !== undefined && !scrollUpdateWasRequested) {
      if (!isProgramScroll.current) {
        scrollPosition.current = scrollOffset;
      }

      setTimeout(() => {
        isProgramScroll.current = false;
      }, 0);
    }
  };

  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const dimensionsRef = useRef({ width: 0, height: 0 });
  useEffect(() => {
    if (listRef.current) {
      listRef.current.resetAfterIndex(1, true); // Reset all items
    }
  }, [dimensions]);

  return (
    <>
      <div
        className={`h-full w-full flex flex-col flex-1 overflow-y-auto pb-2`}
        style={{ maxHeight: activeVideoPageId && 'calc(100% - 180px)' }}
      >
        <AutoSizer>
          {({ height, width }: any) => {
            secondaryViewSize.current = width;
            if (dimensionsRef.current.width !== width || dimensionsRef.current.height !== height) {
              dimensionsRef.current = { width, height };
              // Schedule the state update outside the render phase
              requestAnimationFrame(() => setDimensions({ width, height }));
            }
            // Trigger the resize handler when the dimensions change
            return (
              <div className="relative">
                <InfiniteLoader
                  isItemLoaded={isItemLoaded}
                  itemCount={timelines.length + (nextUrl ? 1 : 0)}
                  loadMoreItems={loadMoreItems}
                >
                  {({ onItemsRendered, ref }) => (
                    <VariableSizeList
                      height={height}
                      itemCount={timelines.length + (nextUrl ? 1 : 0)}
                      onItemsRendered={onItemsRendered}
                      ref={(list) => {
                        // Assigning the ref to the provided ref and calling resetAfterIndex when necessary
                        ref(list);
                        if (list) {
                          listRef.current = list;
                        }
                      }}
                      width={width}
                      onScroll={(scrollInfo) => {
                        handleScrollY(scrollInfo); // Call the scroll handler
                      }}
                      itemSize={getItemSize}
                    >
                      {({ index, style }) => {
                        const timeline = timelines[index]; // Assuming your timeline data is an array
                        return (
                          <ItemRenderer
                            key={timeline?.hash_id} // Ensure a unique key is assigned here, assuming each timeline has a unique `id`
                            style={style} // Don't forget to pass the style prop if required for positioning
                            index={index}
                            timeline={timeline} // Pass the timeline data to your ItemRenderer
                          />
                        );
                      }}
                    </VariableSizeList>
                  )}
                </InfiniteLoader>
              </div>
            );
          }}
        </AutoSizer>
      </div>
      {activeVideoPageId && (
        <AddForm
          channelId={channelId}
          onSubmit={handleAddSummary}
          textinputPlaceholder={'Add a summary sentence to the video'}
          selected={selected}
        ></AddForm>
      )}
    </>
  );
}

export default memo(TimelineView);
